interface EntityConfig {
  baseEndpoint: string;
  entityName: string;
}

class EntityService {
  private config: EntityConfig;

  constructor(config: EntityConfig) {
    this.config = config;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Aucun token d\'authentification trouvé');
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      // On preserve le statut HTTP sur l'erreur pour que les pages puissent distinguer
      // un 404 (ressource introuvable -> page 404) d'une autre erreur.
      const err = new Error(`Erreur HTTP: ${response.status}`) as Error & { status?: number };
      err.status = response.status;
      throw err;
    }

    return response.json();
  }

  async getById(id: string): Promise<any> {
    // Utiliser directement l'API réelle
    const url = this.config.baseEndpoint.replace('{id}', id);
    const data = await this.fetchWithAuth(url);

    // CORRECTION: Gestion spécialisée pour les commandes et opérations BC/BCI prestataires
    if (this.config.entityName === 'CommandeBC' || this.config.entityName === 'CommandeBCI' ||
        this.config.entityName === 'OperationBC' || this.config.entityName === 'OperationBCI') {
      if (data.status !== 'success' && data.status !== 'debug') {
        throw new Error('Format de réponse API inattendu');
      }

      if (!data.data) {
        throw new Error(`Aucune donnée de ${this.config.entityName} trouvée`);
      }

      // Pour les commandes et opérations BC/BCI, retourner les données directement adaptées
      return this.adaptApiData(data.data);
    }

    // Pour les autres types d'entités
    if (data.status !== 'success' || !data.data) {
      throw new Error('Format de réponse API inattendu');
    }

    return this.adaptApiData(data.data);
  }

  async launchConsultation(id: string, entity: any): Promise<void> {
    // CORRECTION: Logique spécifique pour les opérations ET commandes BC/BCI
    if (this.config.entityName === 'OperationBC' || this.config.entityName === 'CommandeBC' || 
        this.config.entityName === 'OperationBCI' || this.config.entityName === 'CommandeBCI') {
      // Calcul des dates si nécessaire
      const calculerDates = () => {
        const dateDebutConsult = new Date();
        const delaisConsult = 10; // 10 jours par défaut pour les BC/BCI

        const dateFinConsult = new Date(dateDebutConsult);
        dateFinConsult.setDate(dateDebutConsult.getDate() + delaisConsult);

        const dateDebutOp = new Date(dateFinConsult);
        dateDebutOp.setDate(dateFinConsult.getDate() + 1);

        const delaisOperation = entity.formulaire?.delaisFinOperation || 30;
        const dateFinOp = new Date(dateDebutOp);
        dateFinOp.setDate(dateDebutOp.getDate() + delaisOperation);

        return {
          dateDebutOperation: dateDebutOp.toISOString().split('T')[0],
          dateFinOperation: dateFinOp.toISOString().split('T')[0]
        };
      };

      // Récupérer les dates existantes ou calculer de nouvelles
      let dateDebutOperation = entity.formulaire?.dateDebutOperation || entity.date_debut_operation || '';
      let dateFinOperation = entity.formulaire?.dateFinOperation || entity.date_fin_operation || '';

      if (!dateDebutOperation || dateDebutOperation === '') {
        const dates = calculerDates();
        dateDebutOperation = dates.dateDebutOperation;
      }

      if (!dateFinOperation || dateFinOperation === '') {
        const dates = calculerDates();
        dateFinOperation = dates.dateFinOperation;
      }

      // CORRECTION: Mapping correct des champs pour l'API BC/BCI avec ajout des dates
      const payload = {
        nom: entity.formulaire?.nomOperation || entity.nom || '',
        reference: entity.formulaire?.referenceInterne || entity.reference || '',
        adresse: entity.formulaire?.adresse || entity.adresse || '',
        code_postal: entity.formulaire?.codePostal || entity.code_postal || '',
        ville: entity.formulaire?.ville || entity.ville || '',
        // CORRECTION: facturation = ancien coordonnées maîtrise
        facturation: entity.formulaire?.coordonneesMaitrise || entity.coordonnees_maitrise || '',
        // CORRECTION: coordonnees_maitrise = ancien coordonnées maîtrise d'ouvrage
        coordonnees_maitrise: entity.formulaire?.coordonneesMaitriseDOuvrage || entity.coordonnees_maitrise_ouvrage || '',
        consultation: "10", // Toujours 10 jours pour les BC/BCI
        besoin: entity.formulaire?.besoin || entity.besoin || '',
        visite: entity.formulaire?.visitePreavis ? "1" : "0",
        condition_visite: entity.formulaire?.conditionVisite || entity.condition_visite || '',
        // Ajout des dates d'opération
        date_debut_operation: dateDebutOperation,
        date_fin_operation: dateFinOperation
      }; console.log('Payload BC/BCI consultation (mapping corrigé):', payload);

      // CORRECTION: Endpoint différent pour les commandes BC/BCI et opérations BC/BCI
      let endpoint = '';
      if (this.config.entityName === 'CommandeBC') {
        endpoint = `/bdc/commande/${id}/consultation`;
      } else if (this.config.entityName === 'CommandeBCI') {
        endpoint = `/bdci/commande/${id}/consultation`;
      } else if (this.config.entityName === 'OperationBC') {
        endpoint = `/bdc/operation/${id}/consultation`;
      } else if (this.config.entityName === 'OperationBCI') {
        endpoint = `/bdci/operation/${id}/consultation`;
      }

      await this.fetchWithAuth(endpoint, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
    } else {
      // Logique existante pour MS (inchangée)
      const calculerDates = () => {
        const dateDebutConsult = entity.formulaire?.dateDebutConsultation
          ? new Date(entity.formulaire.dateDebutConsultation)
          : new Date();

        const delaisConsult = entity.formulaire?.delaisConsultation || 30;
        const delaisOperation = entity.formulaire?.delaisFinOperation || 30;

        const dateFinConsult = new Date(dateDebutConsult);
        dateFinConsult.setDate(dateDebutConsult.getDate() + delaisConsult);

        const dateDebutOp = new Date(dateFinConsult);
        dateDebutOp.setDate(dateFinConsult.getDate() + 1);

        const dateFinOp = new Date(dateDebutOp);
        dateFinOp.setDate(dateDebutOp.getDate() + delaisOperation);

        return {
          dateDebutOperation: dateDebutOp.toISOString().split('T')[0],
          dateFinOperation: dateFinOp.toISOString().split('T')[0]
        };
      };

      let dateDebutOperation = entity.formulaire?.dateDebutOperation;
      let dateFinOperation = entity.formulaire?.dateFinOperation;

      if (!dateDebutOperation || dateDebutOperation === '') {
        const dates = calculerDates();
        dateDebutOperation = dates.dateDebutOperation;
      }

      if (!dateFinOperation || dateFinOperation === '') {
        const dates = calculerDates();
        dateFinOperation = dates.dateFinOperation;
      }

      const payload = {
        [this.config.entityName.toLowerCase()]: {
          ...entity,
          date_debut_operation: dateDebutOperation,
          date_fin_operation: dateFinOperation,
        },
        id_lot_departement: entity?.id_lot_departement,
        id_lot_technique: entity?.id_lot_technique,
        date_debut_operation: dateDebutOperation,
        date_fin_operation: dateFinOperation
      };

      // Construire l'URL de base en retirant /{id}
      const baseUrl = this.config.baseEndpoint.replace('/{id}', '');
      await this.fetchWithAuth(`${baseUrl}/${id}/consultation`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
    }
  }

  async validate(entity: any, techNotes: Record<string, any>, selectedPrestataire: number | null): Promise<void> {
    const isOperation = this.config.entityName === 'Operation' || this.config.entityName === 'OperationBC';
    const entities = isOperation ? entity.commandes : entity.prestataires;
    const selectedEntity = entities?.[selectedPrestataire || 0];

    // Calcul des dates si nécessaires
    const calculerDates = () => {
      const today = new Date();
      const dateFinConsult = new Date(today);
      dateFinConsult.setDate(today.getDate() + 10); // 10 jours de consultation

      const dateDebutOp = new Date(dateFinConsult);
      dateDebutOp.setDate(dateFinConsult.getDate() + 1);

      const delaisOperation = entity.formulaire?.delaisFinOperation || 30;
      const dateFinOp = new Date(dateDebutOp);
      dateFinOp.setDate(dateDebutOp.getDate() + delaisOperation);

      return {
        dateDebutOperation: dateDebutOp.toISOString().split('T')[0],
        dateFinOperation: dateFinOp.toISOString().split('T')[0]
      };
    };

    // Récupérer les dates ou en calculer de nouvelles
    let dateDebutOperation = entity.formulaire?.dateDebutOperation || entity.date_debut_operation || '';
    let dateFinOperation = entity.formulaire?.dateFinOperation || entity.date_fin_operation || '';

    if (!dateDebutOperation || dateDebutOperation === '') {
      const dates = calculerDates();
      dateDebutOperation = dates.dateDebutOperation;
    }

    if (!dateFinOperation || dateFinOperation === '') {
      const dates = calculerDates();
      dateFinOperation = dates.dateFinOperation;
    }

    // Construire le payload avec les dates
    const payload = {
      [`${this.config.entityName.toLowerCase()}_id`]: entity.id,
      // Ajout des dates d'opération pour tous les types d'entités
      date_debut_operation: dateDebutOperation,
      date_fin_operation: dateFinOperation,
      id_signataire: entity.id_signataire,
      type_signataire: entity.type_signataire,
      [isOperation ? 'commandes' : 'prestataires']: entities?.map((item: any, index: number) => {
        const notes = techNotes[index] || {};
        return {
          [`${isOperation ? 'commande' : 'prestataire'}_id`]: item.id,
          [`${isOperation ? 'id_note_technique' : ''}`]: isOperation ? item.id_note_technique : undefined,
          selected: isOperation ?
            item.id === selectedEntity?.id :
            index === selectedPrestataire,
          note_operationnel: notes.organisation?.note ?? 0,
          commentaire_operationnel: notes.organisation?.commentaire ?? "",
          description_operationnel: notes.organisation?.description ?? "",
          note_humain: notes.moyens?.note ?? 0,
          commentaire_humain: notes.moyens?.commentaire ?? "",
          description_humain: notes.moyens?.description ?? "",
          note_environnement: notes.social?.note ?? 0,
          commentaire_environnement: notes.social?.commentaire ?? "",
          description_environnement: notes.social?.description ?? "",
        };
      }),
    };

    // Construire l'URL correctement
    const baseUrl = this.config.baseEndpoint.replace('/{id}', '');
    const endpoint = isOperation ?
      `${baseUrl}/${entity.id}/validate` :
      `${baseUrl}/${entity.id}/note`;

    await this.fetchWithAuth(endpoint, {
      method: isOperation ? "POST" : "PUT",
      body: JSON.stringify(payload),
    });
  }

  async refuse(id: string): Promise<void> {
    const baseUrl = this.config.baseEndpoint.replace('/{id}', '');
    await this.fetchWithAuth(`${baseUrl}/${id}/annuler`, {
      method: 'POST',
    });
  }

  async delete(id: string): Promise<void> {
    const baseUrl = this.config.baseEndpoint.replace('/{id}', '');
    await this.fetchWithAuth(`${baseUrl}/${id}`, {
      method: 'DELETE',
    });
  }

  private adaptApiData(apiData: any): any {
    // CORRECTION: Adaptation spécialisée pour les commandes BC/BCI prestataires
    if (this.config.entityName === 'CommandeBC' || this.config.entityName === 'CommandeBCI') {
      return {
        ...apiData,
        // Conserver la structure originale pour les commandes BC/BCI prestataires
        // car les données sont déjà dans le bon format selon l'exemple API fourni
        formulaire: {
          // Mapper depuis operation pour l'affichage dans les formulaires
          nomOperation: apiData.operation?.nom || '',
          referenceInterne: apiData.operation?.reference || '',
          delaisConsultation: 10, // Toujours 10 pour les BC/BCI
          besoin: apiData.operation?.besoin || '',
          visitePreavis: apiData.operation?.visite === "1" || apiData.operation?.visite === true || apiData.operation?.visite === 1,
          conditionVisite: apiData.operation?.condition_visite || '',
          adresse: apiData.operation?.adresse || '',
          codePostal: apiData.operation?.code_postal || '',
          ville: apiData.operation?.ville || '',
          // CORRECTION: Mapping pour les coordonnées BC/BCI prestataires
          coordonneesMaitriseDOuvrage: apiData.operation?.facturation || '',
          coordonneesMaitrise: apiData.operation?.coordonnees_maitrise_ouvrage || '',
          dateDebutOperation: apiData.operation?.date_debut_operation || '',
          dateFinOperation: apiData.operation?.date_fin_operation || '',
        }
      };
    }

    // CORRECTION: Adaptation avec le mapping corrigé des champs pour OperationBC/BCI
    return {
      ...apiData,
      formulaire: {
        nomOperation: apiData.nom || apiData.titre || '',
        referenceInterne: apiData.reference || apiData.numeroDeCommande || '',
        delaisConsultation: 10, // Toujours 10 pour les BC/BCI
        delaisFinOperation: apiData.delai_execution || apiData.delais || 30,
        besoin: apiData.besoin || '',
        visitePreavis: apiData.visite === "1" || apiData.visite === true || apiData.visite === 1,
        conditionVisite: apiData.condition_visite || apiData.conditionVisite || '',
        adresse: apiData.adresse || '',
        codePostal: apiData.code_postal || '',
        ville: apiData.ville || '',
        // CORRECTION FINALE: Pour OperationBC, mapper correctement depuis l'API
        // facturation (API) → coordonneesMaitriseDOuvrage (Frontend - "Facturation suivie par :")
        coordonneesMaitriseDOuvrage: apiData.facturation || '',
        // coordonnees_maitrise_ouvrage (API) → coordonneesMaitrise (Frontend - "Coordonnées de la maîtrise d'oeuvre:")
        coordonneesMaitrise: apiData.coordonnees_maitrise_ouvrage || '',
        dateDebutOperation: apiData.date_debut_operation || '',
        dateFinOperation: apiData.date_fin_operation || '',
        dateDebutConsultation: apiData.date_debut_consultation || '',
        sections: apiData.sections || []
      }
    };
  }
}

// Instances spécifiques
export const operationService = new EntityService({
  baseEndpoint: '/subsequents/operation/{id}',
  entityName: 'Operation'
});

export const operationBCService = new EntityService({
  baseEndpoint: '/bdc/operation/{id}',
  entityName: 'OperationBC'
});

export const commandeService = new EntityService({
  baseEndpoint: '/commande-ms/{id}',
  entityName: 'Commande'
});

export const commandeBCService = new EntityService({
  baseEndpoint: '/bdc/commande/{id}',
  entityName: 'CommandeBC'
});

export const commandeBCIService = new EntityService({
  baseEndpoint: '/bdci/commande/{id}',
  entityName: 'CommandeBCI'
});

export const operationBCIService = new EntityService({
  baseEndpoint: '/bdci/operation/{id}',
  entityName: 'OperationBCI'
});